import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import { LanguageProvider } from './contexts/LanguageContext';
import { FormProvider } from './contexts/FormContext';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import './i18n';
import './App.css';

// Public pages
import HomePage from './pages/HomePage';
import BookingPage from './pages/BookingPage';
import EmergencyPage from './pages/EmergencyPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalNoticePage from './pages/LegalNoticePage';
import ImplantsPage from './pages/ImplantsPage';
import VeneersPage from './pages/VeneersPage';
import TechnologiesPage from './pages/TechnologiesPage';
import EmergencyButton from './components/EmergencyButton';
import LanguageTest from './components/LanguageTest';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';

// Admin components
import LoginPage from './components/admin/LoginPage';
import AdminLayout from './components/admin/AdminLayout';
import Dashboard from './components/admin/Dashboard';
import CalendarView from './components/admin/CalendarView';

// Protected route component for admin
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-navy-admin"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/admin/login" replace />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Admin Routes - separate layout (outside LanguageProvider) */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="calendar" element={<CalendarView />} />
            <Route path="appointments" element={<div>Appointments List (Coming Soon)</div>} />
            <Route path="providers" element={<div>Providers Management (Coming Soon)</div>} />
            <Route path="procedures" element={<div>Procedures Management (Coming Soon)</div>} />
            <Route path="operatories" element={<div>Operatories Management (Coming Soon)</div>} />
            <Route path="reports" element={<div>Reports (Coming Soon)</div>} />
          </Route>

          {/* Public Website Routes - original layout (with LanguageProvider) */}
          <Route path="*" element={
            <LanguageProvider>
              <FormProvider>
                <div className="flex flex-col min-h-screen">
                  <Header />
                  <div className="flex-grow">
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/book" element={<Navigate to="/kontakt?booking=true" replace />} />
                      <Route path="/emergency" element={<EmergencyPage />} />
                      <Route path="/kontakt" element={<ContactPage />} />
                      <Route path="/implantate" element={<ImplantsPage />} />
                      <Route path="/veneers" element={<VeneersPage />} />
                      <Route path="/technologien" element={<TechnologiesPage />} />
                      <Route path="/datenschutz" element={<PrivacyPage />} />
                      <Route path="/impressum" element={<LegalNoticePage />} />
                      <Route path="/test" element={<LanguageTest />} />
                      {/* Legacy routes */}
                      <Route path="/booking" element={<Navigate to="/kontakt?booking=true" replace />} />
                      <Route path="/contact" element={<Navigate to="/kontakt" replace />} />
                      <Route path="/implants" element={<Navigate to="/implantate" replace />} />
                      <Route path="/technologies" element={<Navigate to="/technologien" replace />} />
                      <Route path="/legal-notice" element={<Navigate to="/impressum" replace />} />
                      <Route path="/privacy" element={<Navigate to="/datenschutz" replace />} />
                    </Routes>
                  </div>
                  <Footer />
                  <EmergencyButton />
                  <CookieConsent />
                </div>
              </FormProvider>
            </LanguageProvider>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
